import { useState } from "react";
import { FileTextIcon, ImageIcon, PaperclipIcon, PlusIcon, RotateCcwIcon } from "lucide-react";

import { SelectorDeFecha } from "@/components/SelectorDeFecha";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import type { ArchivoDeAntecedente } from "@/lib/informes";
import { cn } from "@/lib/utils";

import { lecturaDelContexto } from "./contextoDelArchivo";
import { esImagen, fechaDeSubida } from "./documentosDeLaPestana";
import { SelectorDeClasificacion, type Clasificacion } from "./SelectorDeClasificacion";
import { formatearPeso } from "./ZonaDeImagen";

/**
 * La fecha de EMISIÓN de un documento, corregible a mano (rebanada 2 de
 * `odd/tasks/fecha-del-documento-y-tasacion-mas-reciente.md`).
 *
 * ══ SOLO SE DIBUJA CUANDO `fecha_aplica` Y AUNQUE NO HAYA FECHA ═════════════
 *
 * `fecha_aplica` lo decide el BACKEND —si este archivo es de un tipo que
 * `CampoDelRelleno::GRUPO_DE_TASACIONES` declara tasación—, nunca este
 * componente. Y se dibuja SIN fecha: es el caso que más importa, porque es
 * justamente el archivo donde la IA no encontró nada y hace falta poder
 * escribirla.
 *
 * ══ ESTADO LOCAL + "GUARDAR" EXPLÍCITO, NUNCA UN PATCH POR TECLA ════════════
 *
 * `SelectorDeFecha` avisa en CADA pulsación —su máscara emite `onChange` por
 * carácter, y `""` mientras el texto todavía no forma una fecha completa—,
 * así que cablearlo derecho al servidor sería el mismo defecto que ya mordió
 * con el slider de referencias y el buscador del paso 1: una escritura por
 * tecla, y encima una que VACIARÍA la fecha en cada pulsación intermedia. El
 * botón "Guardar" solo aparece cuando lo tecleado (o elegido en el
 * calendario) difiere de lo persistido, y es el único que dispara el `PATCH`.
 *
 * VACIAR usa el MISMO botón: borrar el texto deja el valor en `""`, distinto
 * de una fecha ya guardada, así que "Guardar" aparece igual y manda `null` —
 * la misma regla del servidor que vacía las dos columnas juntas.
 */
function FilaDeFechaDeEmision({
  archivo,
  ocupado,
  onCorregirFecha,
}: {
  archivo: ArchivoDeAntecedente;
  ocupado: boolean;
  onCorregirFecha: (archivoId: number, fecha: string | null) => void;
}) {
  const persistida = archivo.documento_fecha ?? "";
  const [valor, setValor] = useState(persistida);
  const [incompleto, setIncompleto] = useState(false);

  // Ajuste en render con lo que llega DE AFUERA —tras guardar, o si otra
  // pestaña ya trajo la propuesta de la IA—, mismo patrón que usa el propio
  // `SelectorDeFecha` para no pisar lo que se está tecleando.
  const [previa, setPrevia] = useState(persistida);
  if (persistida !== previa) {
    setPrevia(persistida);
    setValor(persistida);
  }

  const id = `fecha-emision-${archivo.id}`;
  const hayCambios = !incompleto && valor !== persistida;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-6">
      <label htmlFor={id} className="text-ink-hint text-xs font-semibold">
        Fecha de emisión
      </label>

      <SelectorDeFecha
        id={id}
        value={valor}
        onChange={setValor}
        onTextoIncompleto={setIncompleto}
        disabled={ocupado}
        invalido={incompleto}
        className="w-36"
      />

      {/* PROPUESTA POR LA IA: una marca DISCRETA y no una alarma, para que se
          entienda que conviene revisarla antes de confiar en ella. Con
          origen `manual` no se dibuja nada — ya la revisó una persona. */}
      {archivo.documento_fecha_origen === "ia" ? (
        <span className="text-ink-hint text-xs">· Propuesta por la IA</span>
      ) : null}

      {hayCambios ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={ocupado}
          aria-label={`Guardar fecha de emisión de ${archivo.nombre_original}`}
          onClick={() => onCorregirFecha(archivo.id, valor || null)}
        >
          Guardar
        </Button>
      ) : null}
    </div>
  );
}

/** Espejo del `max:5000` de `AnotarElArchivoRequest`. */
const MAXIMO_DE_LA_NOTA = 5000;

/**
 * La nota del tasador sobre UN archivo (`odd/tasks/notas-por-archivo.md`): lo
 * que él sabe del documento y el documento no dice. La IA la lee junto con el
 * texto del archivo; el cliente nunca la ve.
 *
 * ══ SE GUARDA EN EL ACTO Y NO CUENTA COMO CAMBIO SIN GUARDAR ════════════════
 *
 * Misma división que la fecha de emisión: es una petición propia sobre la fila
 * del archivo, así que "Guardar cambios" de la pestaña no la arrastra ni la
 * espera. El texto se edita en estado local y viaja solo con "Guardar nota" —
 * nunca por tecla—; vaciarlo manda `null`.
 *
 * ESTÁ DISPONIBLE PARA CUALQUIER ORIGEN: anotar no es destructivo, igual que
 * corregir la fecha.
 */
function NotaDelArchivo({
  archivo,
  ocupado,
  onAnotar,
}: {
  archivo: ArchivoDeAntecedente;
  ocupado: boolean;
  onAnotar: (archivoId: number, nota: string | null) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState("");
  const id = `nota-archivo-${archivo.id}`;

  const abrir = () => {
    setTexto(archivo.nota ?? "");
    setEditando(true);
  };

  if (editando) {
    return (
      <div className="mt-1.5 flex flex-col gap-1.5 pb-1 pl-6">
        <label htmlFor={id} className="sr-only">
          {`Nota sobre ${archivo.nombre_original}`}
        </label>
        <Textarea
          id={id}
          value={texto}
          maxLength={MAXIMO_DE_LA_NOTA}
          disabled={ocupado}
          onChange={(evento) => setTexto(evento.target.value)}
          placeholder="Agrega el contexto que conoces de este documento y que el archivo no dice."
          className="min-h-16 text-xs"
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={ocupado}
            onClick={() => {
              onAnotar(archivo.id, texto.trim() || null);
              setEditando(false);
            }}
          >
            Guardar nota
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setEditando(false)}
          >
            Cancelar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-start gap-2 pb-1 pl-6">
      {archivo.nota ? (
        <>
          <span className="text-ink-hint text-xs font-semibold">Nota:</span>
          <p className="text-ink-body min-w-0 flex-1 text-xs whitespace-pre-wrap">
            {archivo.nota}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={ocupado}
            aria-label={`Editar nota sobre ${archivo.nombre_original}`}
            onClick={abrir}
          >
            Editar
          </Button>
        </>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={ocupado}
          aria-label={`Agregar nota sobre ${archivo.nombre_original}`}
          onClick={abrir}
        >
          <PlusIcon aria-hidden="true" />
          Agregar nota
        </Button>
      )}
    </div>
  );
}

/** Espejo de `GuardarArchivoDeAntecedenteRequest::MAXIMO_KB`. */
export const MAXIMO_BYTES_DEL_ANTECEDENTE = 32 * 1024 * 1024;

/**
 * Espejo de la regla `mimes:` de `GuardarArchivoDeAntecedenteRequest`.
 *
 * LA LISTA SE AMPLIÓ CUANDO CADA ARCHIVO EMPEZÓ A PRODUCIR SU VERSIÓN EN
 * MARKDOWN: un expediente donde solo entran PDF obliga al cliente a convertir
 * sus documentos antes de mandarlos, y casi nadie lo hace. El servidor la
 * amplió el 13-09-2026 y esta copia se había quedado atrás — el efecto era que
 * el navegador rechazaba un `.docx` que el servidor sí aceptaba, sin llegar a
 * mandarlo, o sea la mitad de los formatos nuevos inalcanzables desde la
 * pantalla.
 *
 * `svg` queda fuera igual que en el servidor: es un documento XML que puede
 * traer scripts y referencias externas.
 */
const TIPOS_ACEPTADOS = [
  "application/pdf",
  // Word
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.oasis.opendocument.text",
  "application/rtf",
  "text/rtf",
  // Excel
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
  "text/csv",
  // PowerPoint
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.presentation",
  // Imágenes
  "image/jpeg",
  "image/png",
  "image/webp",
];

/**
 * Para el `accept` del input: extensiones, que es lo que entiende el diálogo.
 *
 * Van las EXTENSIONES y no los tipos porque el diálogo del sistema operativo
 * filtra por ellas, y porque el tipo que el navegador reporta para un mismo
 * archivo cambia entre sistemas.
 *
 * SE EXPORTA PORQUE EL PORTAL DEL CLIENTE SUBE POR EL MISMO ENDPOINT
 * (`PortalPublicoController::guardarDocumento()` reusa el mismo request). Tenía
 * su propia copia y así fue como quedó desactualizada sin que nada fallara: dos
 * espejos de una sola regla se separan, y el que se separa es el que nadie mira.
 */
export const EXTENSIONES_ACEPTADAS =
  ".pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.ods,.csv,.ppt,.pptx,.odp,.jpg,.jpeg,.png,.webp";

/**
 * Lo que el navegador puede rechazar sin gastar la subida, o `null` si el
 * archivo pasa.
 *
 * NO es la autoridad y no pretende serlo, igual que su gemela de la imagen: la
 * validación real es la del backend, que mira los BYTES con finfo y no el tipo
 * que declara el sistema operativo. Esto existe para no mandar 32 MB por la red
 * antes de descubrir que no entran, y el 422 se pinta igual cuando llega.
 *
 * POR ESO UN TIPO VACÍO PASA. `File.type` sale del sistema operativo y no de
 * los bytes: hay archivos —el `.csv` es el caso corriente— para los que llega
 * en blanco o con el tipo de otra aplicación. Rechazarlos acá sería que el
 * navegador dé por imposible algo que el servidor habría aceptado, y sin
 * ninguna forma de insistir.
 */
export function motivoDeRechazoDelAntecedente(archivo: File): string | null {
  if (archivo.type !== "" && !TIPOS_ACEPTADOS.includes(archivo.type)) {
    // Se nombran las FAMILIAS y no las dieciséis extensiones, igual que el
    // mensaje del servidor: lo lee alguien que está mirando su escritorio, y
    // "Word" le dice más que "doc, docx, odt, rtf".
    return (
      "El archivo debe ser PDF, un documento de Office (Word, Excel, PowerPoint) "
      + "o una imagen (JPG, PNG, WEBP)."
    );
  }
  if (archivo.size > MAXIMO_BYTES_DEL_ANTECEDENTE) {
    return "El archivo no puede pesar más de 32 MB.";
  }
  return null;
}

/** Los tipos que se aceptan, dichos por FAMILIAS como el mensaje de rechazo. */
const TIPOS_PARA_LA_ZONA = "PDF, Word, Excel, PowerPoint o imágenes · máx. 32 MB";

/** Las marcas de origen del diseño: texto en mayúsculas, sobre el gris del DS. */
const ETIQUETA_DE_ORIGEN = { cliente: "CLIENTE", web: "SUBIDO EN LA WEB" } as const;

/**
 * La marca de origen, igual en la fila del documento y en la de cada archivo.
 *
 * Es texto y no solo color: el origen decide qué se puede hacer con el archivo
 * (lo del cliente no se quita) y no puede depender de distinguir un tono.
 */
export function MarcaDeOrigen({
  origen,
  titulo,
}: {
  origen: keyof typeof ETIQUETA_DE_ORIGEN;
  titulo?: string;
}) {
  return (
    <span
      title={titulo}
      className="bg-muted text-ink-body flex-none rounded-sm px-1.5 py-px text-[0.625rem] font-extrabold tracking-[0.03em]"
    >
      {ETIQUETA_DE_ORIGEN[origen]}
    </span>
  );
}

/**
 * UN archivo recibido: nombre, peso, origen, fecha de subida y sus acciones.
 *
 * "QUITAR" PIDE CONFIRMACIÓN EN LA MISMA FILA ("¿Quitar? Sí / No"), no en un
 * diálogo: borrar es irreversible —no hay borrado lógico, el objeto se va del
 * bucket— pero el caso corriente es el archivo equivocado, y un diálogo por
 * cada uno vuelve pesado limpiar un lote. El estado de la pregunta es local de
 * la fila: no tiene sentido fuera de ella.
 *
 * LO DEL CLIENTE NO TIENE "QUITAR", ni siquiera con confirmación: es la prueba
 * de que cumplió y el portal puede estar cerrado. El servidor lo custodia igual
 * (403); esconder el botón es no ofrecer una acción que va a fallar.
 */
function FilaDeArchivo({
  archivo,
  urlDeDescarga,
  ocupado,
  onQuitar,
  onCorregirFecha,
  onAnotar,
  onReprocesar,
  archivoReprocesando,
  clasificacion,
}: {
  archivo: ArchivoDeAntecedente;
  urlDeDescarga: (archivoId: number) => string;
  ocupado: boolean;
  onQuitar: (archivoId: number) => void;
  onCorregirFecha: (archivoId: number, fecha: string | null) => void;
  onAnotar: (archivoId: number, nota: string | null) => void;
  onReprocesar: (archivoId: number) => void;
  archivoReprocesando: number | null;
  clasificacion?: Clasificacion;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [cambiandoElTipo, setCambiandoElTipo] = useState(false);
  const contexto = lecturaDelContexto(archivo);
  const reprocesando = archivoReprocesando === archivo.id;
  const Icono = esImagen(archivo.nombre_original) ? ImageIcon : FileTextIcon;
  const delCliente = archivo.origen === "cliente";

  return (
    <li className="bg-muted/40 rounded-[0.625rem] border px-2.5 py-1">
      <div className="flex min-h-8 flex-wrap items-center gap-x-2.5 gap-y-1">
        <Icono className="text-ink-hint size-3.5 flex-none" aria-hidden="true" />

        <span
          className="text-ink min-w-0 flex-1 truncate text-[0.78125rem] font-semibold"
          title={archivo.nombre_original}
        >
          {archivo.nombre_original}
        </span>

        <span className="text-ink-muted flex-none font-mono text-[0.65625rem] font-semibold">
          {formatearPeso(archivo.archivo_bytes)}
        </span>

        {/* QUIÉN lo dejó. `subido_por` viene `null` para lo que manda el cliente
            —no tiene cuenta—, así que el origen sale de `origen` y el nombre de
            quien lo subió viaja en el título de la marca. */}
        <MarcaDeOrigen
          origen={delCliente ? "cliente" : "web"}
          titulo={!delCliente && archivo.subido_por ? `Subido por ${archivo.subido_por}` : undefined}
        />

        <span className="text-ink-muted min-w-[4.625rem] flex-none text-right font-mono text-[0.65625rem] font-semibold">
          {fechaDeSubida(archivo.subido_en)}
        </span>

        {/* EN QUÉ QUEDÓ SU VERSIÓN EN MARKDOWN, la que lee la IA. Se dibuja en
            los cuatro estados, incluido "sin procesar": la ausencia de estado es
            un caso real y sin rótulo sería indistinguible de un archivo ya
            legible. */}
        <Badge variant={contexto.variante}>{contexto.etiqueta}</Badge>

        {/* "REINTENTAR" SOLO CUANDO EL SERVIDOR LO MARCA reintentable, para
            cualquier origen: no borra nada del expediente. */}
        {archivo.contexto_reintentable ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={ocupado || reprocesando}
            onClick={() => onReprocesar(archivo.id)}
            aria-label={`Reintentar ${archivo.nombre_original}`}
          >
            {reprocesando ? <Spinner /> : <RotateCcwIcon aria-hidden="true" />}
          </Button>
        ) : null}

        {confirmando ? (
          <span className="inline-flex flex-none items-center gap-1.5">
            <span className="text-ink-body text-[0.71875rem] font-bold">¿Quitar?</span>
            <button
              type="button"
              disabled={ocupado}
              aria-label={`Sí, quitar ${archivo.nombre_original}`}
              onClick={() => {
                setConfirmando(false);
                onQuitar(archivo.id);
              }}
              className="bg-destructive rounded-md px-2 py-0.5 text-[0.71875rem] font-bold text-white disabled:opacity-50"
            >
              Sí
            </button>
            <button
              type="button"
              aria-label={`No quitar ${archivo.nombre_original}`}
              onClick={() => setConfirmando(false)}
              className="text-ink-body border-input rounded-md border-[1.5px] px-2 py-px text-[0.71875rem] font-bold"
            >
              No
            </button>
          </span>
        ) : (
          <span className="inline-flex flex-none gap-0.5">
            {/* Un enlace y no un botón: es una descarga, así que el menú
                contextual del navegador tiene que funcionar. La cookie de
                sesión viaja sola porque `api.*` y `app.*` comparten dominio. */}
            <a
              href={urlDeDescarga(archivo.id)}
              aria-label={`Ver ${archivo.nombre_original}`}
              className="text-ink-body hover:bg-muted rounded-md px-2 py-1 text-[0.71875rem] font-bold"
            >
              Ver
            </a>
            {/* CAMBIAR EL TIPO vale para TODO archivo, también el del equipo y
                el del cliente: no borra nada, solo lo cuelga de otro documento.
                "Devolver" solo existe para lo que llegó por el portal, que es
                lo único que puede estar sin clasificar. */}
            {clasificacion ? (
              <>
                <button
                  type="button"
                  disabled={ocupado}
                  aria-label={`Cambiar tipo de ${archivo.nombre_original}`}
                  aria-expanded={cambiandoElTipo}
                  onClick={() => setCambiandoElTipo((abierto) => !abierto)}
                  className="text-ink-body hover:bg-muted rounded-md px-2 py-1 text-[0.71875rem] font-bold disabled:opacity-50"
                >
                  Cambiar tipo
                </button>
                {archivo.seccion_cliente ? (
                  <button
                    type="button"
                    disabled={ocupado}
                    aria-label={`Devolver ${archivo.nombre_original} a sin clasificar`}
                    onClick={() => void clasificacion.onClasificar(archivo.id, null)}
                    className="text-ink-body hover:bg-muted rounded-md px-2 py-1 text-[0.71875rem] font-bold disabled:opacity-50"
                  >
                    Devolver a sin clasificar
                  </button>
                ) : null}
              </>
            ) : null}
            {delCliente ? null : (
              <button
                type="button"
                disabled={ocupado}
                aria-label={`Quitar ${archivo.nombre_original}`}
                onClick={() => setConfirmando(true)}
                className="text-danger-soft-foreground hover:bg-danger-soft rounded-md px-2 py-1 text-[0.71875rem] font-bold disabled:opacity-50"
              >
                Quitar
              </button>
            )}
          </span>
        )}
      </div>

      {cambiandoElTipo && clasificacion ? (
        <SelectorDeClasificacion
          nombreDelArchivo={archivo.nombre_original}
          clasificacion={clasificacion}
          excluirTipoId={archivo.documento_tipo_id}
          onClasificar={(tipoId) => clasificacion.onClasificar(archivo.id, tipoId)}
          onCerrar={() => setCambiandoElTipo(false)}
        />
      ) : null}

      {/* LA FECHA DE EMISIÓN, solo si este tipo de documento es una tasación
          (`fecha_aplica`, decidido por el backend). El diseño no tiene un lugar
          para ella: va bajo la fila, que es un dato que se corrige. */}
      {archivo.fecha_aplica ? (
        <FilaDeFechaDeEmision
          archivo={archivo}
          ocupado={ocupado}
          onCorregirFecha={onCorregirFecha}
        />
      ) : null}

      {/* LA NOTA DEL TASADOR, para cualquier origen y cualquier tipo. */}
      <NotaDelArchivo archivo={archivo} ocupado={ocupado} onAnotar={onAnotar} />

      {/* QUÉ HACER, cuando hay algo que hacer. En su propia línea y no en un
          `title`: un consejo escondido detrás del puntero no existe para quien
          no pasa por encima. */}
      {contexto.detalle ? (
        <p className="text-ink-muted mt-1 pb-1 pl-6 text-xs">{contexto.detalle}</p>
      ) : null}
    </li>
  );
}

/**
 * Lo que se despliega bajo un documento: sus archivos y la zona para soltar más.
 *
 * Presentacional a propósito —no sabe de la API—, igual que {@see ZonaDeImagen}.
 * El selector de archivos NO vive acá sino en la fila: el botón "Adjuntar" de
 * arriba tiene que poder abrirlo con esta parte plegada.
 *
 * VARIOS ARCHIVOS Y NINGÚN REEMPLAZO, al revés que la imagen del bloque: de un
 * mismo documento llegan el informe, sus anexos y la planilla. La acción es
 * "agregar" y "quitar" — nunca "cambiar".
 */
export function ArchivosDelDocumento({
  archivos,
  urlDeDescarga,
  ocupado = false,
  onAbrirSelector,
  onSoltar,
  onQuitar,
  onCorregirFecha,
  onAnotar,
  onReprocesar,
  archivoReprocesando,
  clasificacion,
}: {
  archivos: ArchivoDeAntecedente[];
  /** Cómo armar el enlace de descarga de un archivo. */
  urlDeDescarga: (archivoId: number) => string;
  /** Hay una subida o un borrado en curso EN ESTE documento. */
  ocupado?: boolean;
  /** Abre el selector de archivos del documento (clic en la zona punteada). */
  onAbrirSelector: () => void;
  /** Los archivos soltados sobre la zona, TODOS juntos y en orden. */
  onSoltar: (archivos: File[]) => void;
  onQuitar: (archivoId: number) => void;
  /** Corrige la fecha de EMISIÓN. `fecha` en `null` vacía la corrección. */
  onCorregirFecha: (archivoId: number, fecha: string | null) => void;
  /** Escribe la nota del tasador sobre un archivo. `nota` en `null` la vacía. */
  onAnotar: (archivoId: number, nota: string | null) => void;
  /**
   * "Reintentar" un archivo trabado o fallido
   * (`odd/tasks/transcripcion-en-limbo.md`). Para CUALQUIER origen.
   */
  onReprocesar: (archivoId: number) => void;
  /** El id del archivo con un reproceso EN VUELO, o `null`. */
  archivoReprocesando: number | null;
  /** Para cambiar el tipo de un archivo; sin esto la fila no lo ofrece. */
  clasificacion?: Clasificacion;
}) {
  const [encima, setEncima] = useState(false);

  return (
    <div className="flex flex-col gap-1.5 pt-0.5 pr-4 pb-3.5 pl-10">
      <ul className="flex flex-col gap-1.5">
        {archivos.map((archivo) => (
          <FilaDeArchivo
            key={archivo.id}
            archivo={archivo}
            urlDeDescarga={urlDeDescarga}
            ocupado={ocupado}
            onQuitar={onQuitar}
            onCorregirFecha={onCorregirFecha}
            onAnotar={onAnotar}
            onReprocesar={onReprocesar}
            archivoReprocesando={archivoReprocesando}
            clasificacion={clasificacion}
          />
        ))}
      </ul>

      {/* CLIC O ARRASTRE: el mismo camino. Lo soltado se valida y se sube
          exactamente como lo elegido en el diálogo, así que un archivo que el
          diálogo rechazaría no entra por acá. */}
      <button
        type="button"
        disabled={ocupado}
        onClick={onAbrirSelector}
        onDragOver={(evento) => {
          evento.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(evento) => {
          evento.preventDefault();
          setEncima(false);
          const soltados = Array.from(evento.dataTransfer?.files ?? []);
          if (soltados.length > 0) onSoltar(soltados);
        }}
        className={cn(
          "bg-card flex min-h-10 items-center justify-center gap-2 rounded-[0.625rem] border-[1.5px] border-dashed px-3 text-xs font-semibold transition-colors disabled:opacity-60",
          encima
            ? "border-success-dot text-success-soft-foreground"
            : "border-ink-ghost text-ink-muted hover:border-success-dot hover:text-success-soft-foreground",
        )}
      >
        <PaperclipIcon className="size-3.5 flex-none" aria-hidden="true" />
        <span>
          <span className="text-ink font-bold">Adjuntar archivo</span> o arrástralo aquí ·{" "}
          {TIPOS_PARA_LA_ZONA}
        </span>
      </button>
    </div>
  );
}
